import type { User } from 'firebase/auth';
import type { ComponentProps } from 'react';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import React from 'react';
import { z } from 'zod';

import { type RouterOutputs } from '@poseidon/api/types';

import { api } from '@/utils/api';
import isClientSide from './isClientSide';
import { isImpersonationTokenExpired } from './impersonationToken';
import { isSuperadminPath } from './superadminPath';

export const IMPERSONATION_KEY = 'impersonation_session';
/** sessionStorage flag: set right before the reload that ends an expired impersonation
 * (expireImpersonationSession), so a toast can be shown after the reload. */
export const IMPERSONATION_EXPIRED_FLAG = 'impersonation_expired_toast';

/**
 * The route requests act for: the pathname Next has committed, which AuthProvider moves together
 * with its cache reset on navigation. Unset before AuthProvider mounts; the current URL stands in.
 */
let committedPathname: string | undefined;
/** Set once an expired token is found, so requests keep sending it until the reload lands. */
let expiredToken: string | undefined;
let isExpiringImpersonation = false;

const impersonationSessionSchema = z.object({
  token: z.string(),
  email: z.string(),
  userId: z.string(),
  /** Profile picture of the impersonated user */
  profilePicture: z.string().nullish(),
  /** The superadmin's name (e.g., first name) */
  superadminName: z.string().optional(),
  /** The superadmin's team name */
  superadminTeamName: z.string().optional(),
});
type ImpersonationSession = z.infer<typeof impersonationSessionSchema>;

/** The stored session, or null when the value isn't JSON or isn't a session (e.g. an old format without userId). */
function parseImpersonationSession(stored: string): ImpersonationSession | null {
  try {
    const parsed = impersonationSessionSchema.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export type PoseidonApiAuthRes = NonNullable<RouterOutputs['user']['me']>;

export type AuthContextProps = [
  (
    | PoseidonApiAuthRes
    | {
        user: null;
      }
  ) & {
    isPending: boolean;
    isError: boolean;
    impersonation: ImpersonationSession | null;
  },
  {
    startImpersonation: (
      token: string,
      email: string,
      userId: string,
      superadminInfo?: { name?: string; teamName?: string },
      profilePicture?: string | null,
    ) => void;
    stopImpersonation: () => void;
  },
];

const defaultValue: AuthContextProps = [
  {
    user: null,
    isPending: true,
    isError: false,
    impersonation: null,
  },
  {
    startImpersonation: () => {
      throw new Error('AuthProvider not initialized');
    },
    stopImpersonation: () => {
      throw new Error('AuthProvider not initialized');
    },
  },
];

const AuthContext = React.createContext<AuthContextProps>(defaultValue);

export const AuthProvider = ({ children }: ComponentProps<'div'>) => {
  const [firebaseUser, setFirebaseUser] = React.useState<User | null>();
  const [impersonationSession, setImpersonationSession] = React.useState<ImpersonationSession | null>(null);
  const queryClient = useQueryClient();
  const router = useRouter();
  /** The impersonation token requests go out with now — whose data the query cache holds. */
  const actingTokenRef = React.useRef<string | undefined>(undefined);
  /** The Firebase uid last seen (null: signed out), to tell a sign-out or account change from the first sign-in. */
  const signedInUidRef = React.useRef<string | null | undefined>(undefined);

  const {
    data,
    isPending,
    isError,
    refetch: refetchUser,
  } = api.user.me.useQuery(undefined, { enabled: Boolean(firebaseUser) });

  // Every cached response belongs to whoever requests acted as when it was fetched, and most query
  // keys don't name the user — `user.sendsToday({ teamId })` is one key for every rep on a team — so
  // whenever that identity changes, drop it all. resetQueries clears every result and refetches what's
  // mounted; results of requests still in flight as the old identity are discarded (the requests
  // themselves still finish). That includes user.me, which sits in `pending` until the new identity
  // loads, so the app shows its auth loader in between.
  const syncActingIdentity = React.useCallback(() => {
    const next = actingImpersonationToken();
    if (next === actingTokenRef.current) return;
    actingTokenRef.current = next;
    void queryClient.resetQueries();
  }, [queryClient]);

  // Moving between /superadmin pages and the rest of the app while impersonating changes who requests
  // act as. Requests act for the route Next has committed (committedPathname), which this moves
  // together with the cache reset: Next emits beforeHistoryChange, pushes the URL synchronously, then
  // renders the new page in a transition (next/dist/shared/lib/router/router.js,
  // next/dist/client/index.js), so a microtask runs in between — after the URL has moved, before the
  // new page renders. Back/forward emit it too; until then the old page and its requests stay as
  // they were, even though the browser has already moved the URL.
  React.useEffect(() => {
    committedPathname = window.location.pathname;
    actingTokenRef.current = actingImpersonationToken();
    const onBeforeHistoryChange = () => {
      queueMicrotask(() => {
        committedPathname = window.location.pathname;
        syncActingIdentity();
      });
    };
    router.events.on('beforeHistoryChange', onBeforeHistoryChange);
    return () => {
      router.events.off('beforeHistoryChange', onBeforeHistoryChange);
      committedPathname = undefined;
    };
  }, [router.events, syncActingIdentity]);

  React.useEffect(() => {
    if (isClientSide()) {
      const unsubscribe = onAuthStateChanged(getAuth(), (u) => {
        if (u == null) {
          Sentry.setUser(null);
          Sentry.setTag('user_email', undefined);
        } else {
          const sUser = { id: u.uid, email: u.email ?? undefined };
          Sentry.setUser(sUser);
          Sentry.setTag('user_email', u.email ?? undefined);
        }

        // Signing out, or into another account, changes who requests act as too. A stored
        // impersonation belongs to the account that started it, so it goes, along with everything
        // fetched as either of them.
        const uid = u?.uid ?? null;
        if (signedInUidRef.current !== undefined && signedInUidRef.current !== uid) {
          localStorage.removeItem(IMPERSONATION_KEY);
          setImpersonationSession(null);
          actingTokenRef.current = actingImpersonationToken();
          void queryClient.resetQueries();
        }
        signedInUidRef.current = uid;

        setFirebaseUser(u);
        // Force refetch user.me when auth state changes to avoid serving
        // stale cached data (e.g. from a previous session with user: null).
        if (u) {
          void refetchUser();
        }
      });
      return () => unsubscribe();
    }
  }, [setFirebaseUser, refetchUser, queryClient]);

  React.useEffect(() => {
    const sessionData = impersonationSessionGet();
    setImpersonationSession(sessionData);
  }, []);

  // Call after the stored session already says who to act as. On a /superadmin page requests keep
  // running as you, so a switch there leaves the page and its cache alone until you cross into the app.
  const switchIdentity = React.useCallback(
    (session: ImpersonationSession | null) => {
      setImpersonationSession(session);
      syncActingIdentity();
    },
    [syncActingIdentity],
  );

  React.useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // key null: another tab cleared all of localStorage.
      if (event.key !== IMPERSONATION_KEY && event.key !== null) return;
      // Another tab started or stopped impersonating; localStorage is shared, so this tab now acts as them too.
      switchIdentity(impersonationSessionGet());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [switchIdentity]);

  const startImpersonation = React.useCallback(
    (token: string, email: string, userId: string, superadminInfo?: { name?: string; teamName?: string }, profilePicture?: string | null) => {
      const newSession: ImpersonationSession = {
        token,
        email,
        userId,
        profilePicture,
        superadminName: superadminInfo?.name,
        superadminTeamName: superadminInfo?.teamName,
      };
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(newSession));
      switchIdentity(newSession);
    },
    [switchIdentity],
  );

  const stopImpersonation = React.useCallback(() => {
    localStorage.removeItem(IMPERSONATION_KEY);
    switchIdentity(null);
  }, [switchIdentity]);

  // Memoized so consumers re-render when auth changes, not on every render of the app around them.
  const value = React.useMemo<AuthContextProps>(
    () => [
      {
        isPending: firebaseUser === null ? false : isPending,
        isError,
        impersonation: impersonationSession,
        ...(data ?? { user: null }),
      },
      { startImpersonation, stopImpersonation },
    ],
    [firebaseUser, isPending, isError, impersonationSession, data, startImpersonation, stopImpersonation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => React.useContext(AuthContext);

/**
 * The impersonation token requests carry right now, or undefined when they run as the signed-in
 * user: superadmin pages act as you, everywhere else as whoever the stored session names. The single
 * rule behind both the request headers (utils/api.ts) and AuthProvider's cache resets.
 */
export function actingImpersonationToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (expiredToken != null) return expiredToken;
  const session = impersonationSessionGet();
  if (session != null && isImpersonationTokenExpired(session.token)) {
    expireImpersonationSession(session.token);
    return session.token;
  }
  return isSuperadminPath(committedPathname ?? window.location.pathname) ? undefined : session?.token;
}

/**
 * End an impersonation whose token has expired (found here, or rejected by the server with
 * IMPERSONATION_EXPIRED): drop the stored session and reload, which resets everything fetched as
 * them, flagging the reason so _app can explain it after the reload. Until the reload lands, requests
 * keep sending `token`, which the server rejects, rather than quietly answering as you.
 */
export function expireImpersonationSession(token: string | undefined): void {
  if (typeof window === 'undefined' || isExpiringImpersonation) return;
  isExpiringImpersonation = true;
  expiredToken = token;
  localStorage.removeItem(IMPERSONATION_KEY);
  try {
    sessionStorage.setItem(IMPERSONATION_EXPIRED_FLAG, '1');
  } catch {
    // ignore storage access errors (e.g. privacy mode)
  }
  window.location.reload();
}

export function impersonationSessionGet(): ImpersonationSession | null {
  if (typeof window === 'undefined') return null;
  const impersonationSessionStr = localStorage.getItem(IMPERSONATION_KEY);
  if (!impersonationSessionStr) return null;

  const session = parseImpersonationSession(impersonationSessionStr);
  if (session == null) {
    // bad value or old format without userId, clean it
    localStorage.removeItem(IMPERSONATION_KEY);
    return null;
  }
  return session;
}
